/**
 * The words a person sees for how approved work gets into a branch.
 *
 * Inside the workbench approved work is merged onto an integration branch
 * (`mojoup/integration/<target>`) and then "landed" — fast-forwarded — into
 * the branch the checkout is on. Those names stay in code, message names
 * (`land-integrated-work`, Control API `git.land`), setting keys
 * (`git.pushAfterLanding`), prompts to agents and developer logs. On screen it
 * is what git, GitHub and every editor say: work **merges**. "Landing" is
 * insider jargon, and a person should never wonder what it does.
 *
 * Every view, toast, Inbox record and notice takes its words from here, and
 * webview-ui/test/mergeWording.test.ts with src/test/mergeWording.test.ts fail
 * when the old words come back.
 *
 * Plain constants and functions only, no imports: both the host and the
 * renderer read this file. (A DOM-free renderer model that imports it is
 * loaded through `createRequire` in its webview test, as shared/ is CommonJS.)
 */
/** The button that brings approved work into the checked-out branch now. */
export const MERGE_NOW = "Merge now";
/** That button while it works. */
export const MERGING = "Merging…";
/** A stat's or a column's label for work approved but not yet in the checkout. */
export const WAITING_TO_MERGE = "Waiting to merge";
/** "1 approved commit", "3 approved commits". */
export function approvedCommits(n) {
    return `${n} approved commit${n === 1 ? "" : "s"}`;
}
/** "3 approved commits waiting to merge" — or "… waiting to merge into main" when the branch is known. */
export function waitingToMerge(n, target) {
    return `${approvedCommits(n)} waiting to merge${target ? ` into ${target}` : ""}`;
}
/** "Merged into main", "Merged into PLAN-2's branch". */
export function mergedInto(target) {
    return `Merged into ${target}`;
}
/** The notice for a run whose approved work goes straight in: "Approved work merges into main automatically". */
export function mergesAutomatically(target) {
    return `Approved work merges into ${target} automatically`;
}
/**
 * The four stages of a piece of work on its way into the repository, as the
 * Timeline, the Git view and the agent cards say them (webview-ui/src/workStage.ts).
 * The second is where the integration branch is: a person reads "Approved — going in",
 * and the branch's own name appears only in a tooltip or the details.
 */
export const BEING_WORKED_ON = "Being worked on";
export const APPROVED_GOING_IN = "Approved — going in";
export const IN_MAIN = (main = "main") => `In ${main}`;
export const IN_PLAN_BRANCH = (plan) => (plan ? `In ${plan}'s branch` : "In the plan's branch");
/** The tooltip that names the branch approved work collects on: "Approved work collects on mojoup/integration/main before it merges". */
export function approvedBranchTitle(branch) {
    return `Approved work collects on ${branch} before it merges`;
}
//# sourceMappingURL=merging.js.map